import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg } from "@/components/royal/shared";

// Royal Signature — Journal: liquid full-bleed header + featured essay + card grid.
export default function BlogRoyal() {
  const { content } = useContent();
  const s = content?.settings || {};
  const articles = content?.articles || [];
  const [first, ...rest] = articles;
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;
  const heroBg = s.royal_journal_hero_image || first?.image || ROYAL_IMG.botanical;

  return (
    <div className="bg-[#FAF8F5]" data-testid="blog-page">
      <section className="relative flex min-h-[48vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="blog-hero">
        <RoyalLiquidBg src={heroBg} blur={heroBlur} testid="blog-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="blog-eyebrow">The Skin Journal</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="blog-title">
                Skin wisdom, weekly
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-16 pt-12 sm:px-8 lg:px-12">
        {first && (
          <Reveal delay={0.2}>
            <Link to={`/blog/${first.id}`} data-testid={`blog-featured-${first.id}`} className="group mt-12 grid items-center gap-10 lg:grid-cols-2">
              <div className="overflow-hidden rounded-[2rem]">
                <img src={first.image} alt={first.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
              </div>
              <div>
                <p className="eyebrow-royal">{first.category}</p>
                <h2 className="mt-3 font-serif text-3xl font-normal leading-tight text-[#0F0F0F] sm:text-4xl">{first.title}</h2>
                <p className="mt-4 max-w-lg text-sm font-light leading-relaxed text-[#8A8580]">{first.excerpt}</p>
                <p className="mt-5 flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8580]">
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{first.read_time}</span>
                  <span>{first.author}</span>
                </p>
                <span className="royal-link mt-6">
                  Read the Essay <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        {rest.length > 0 && (
          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {rest.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.07}>
                <Link to={`/blog/${a.id}`} data-testid={`blog-card-${a.id}`} className="group block">
                  <div className="overflow-hidden rounded-2xl">
                    <img src={a.image} alt={a.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
                  </div>
                  <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C5A059]">{a.category}</p>
                  <h2 className="mt-2 font-serif text-2xl font-normal leading-snug text-[#0F0F0F] transition-colors group-hover:text-[#C5A059]">{a.title}</h2>
                  <p className="mt-2 line-clamp-2 text-sm font-light leading-relaxed text-[#8A8580]">{a.excerpt}</p>
                  <p className="mt-4 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8580]">
                    <Clock className="h-3 w-3" aria-hidden="true" />{a.read_time}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
        {articles.length === 0 && (
          <p className="mt-16 text-center text-sm font-light text-[#8A8580]" data-testid="blog-empty">Fresh guidance from Dr. Javeria's desk is on its way.</p>
        )}
      </section>
      <RoyalCTA />
    </div>
  );
}
